"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FormulaireDeContact() {
  return <section className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-br from-[hsl(221,70%,45%)] to-[hsl(346,40%,35%)]">
      <FormProvider className="w-full max-w-2xl" formId="contact-form-main" sectionName="formulaire-de-contact" defaultValues={{
      name: "",
      email: "",
      phone: "",
      serviceType: "",
      date: "",
      message: ""
    }} validationRules={{
      name: {
        required: true,
        minLength: 2,
        maxLength: 100
      },
      email: {
        required: true,
        pattern: "email"
      },
      phone: {
        required: false,
        minLength: 10,
        maxLength: 20
      },
      serviceType: {
        required: true
      },
      date: {
        required: false
      },
      message: {
        required: true,
        minLength: 10,
        maxLength: 1000
      }
    }} submitText="Envoyer le message" successMessage="Merci ! Nous vous contacterons bientôt." errorMessage="Échec de l'envoi du formulaire. Veuillez réessayer.">
        <ContactFormContent />
      </FormProvider>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: field1Ref,
    isVisible: field1Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: field2Ref,
    isVisible: field2Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: field3Ref,
    isVisible: field3Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: field4Ref,
    isVisible: field4Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: field5Ref,
    isVisible: field5Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: field6Ref,
    isVisible: field6Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: buttonRef,
    isVisible: buttonVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div ref={formRef} className={`p-8 bg-white/95 backdrop-blur-sm rounded-lg shadow-strong transition-all duration-200 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <p className="text-[hsl(221,70%,45%)] text-lg font-semibold mb-4">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="hover:scale-[1.03] hover:bg-[hsl(221,70%,40%)] hover:text-white transition-all duration-[120ms]">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <div ref={formRef} className={`p-8 bg-white/95 backdrop-blur-sm rounded-lg shadow-strong transition-all duration-200 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <h2 className="text-3xl font-display font-bold text-[hsl(221,70%,45%)] mb-2">
        Contactez-nous
      </h2>
      <p className="text-[hsl(0,0%,30%)] mb-8">
        Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.
      </p>

      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div ref={field1Ref} className={`transition-all duration-200 ${field1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormField control={control} name="name" rules={{
            required: "Le nom est requis",
            minLength: {
              value: 2,
              message: "Le nom doit contenir au moins 2 caractères"
            },
            maxLength: {
              value: 100,
              message: "Le nom ne peut pas dépasser 100 caractères"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input placeholder="Votre nom complet" {...field} className="border-b-2 border-[hsl(221,70%,45%)] focus:border-[hsl(221,70%,40%)] focus:shadow-[0_2px_8px_hsla(221,70%,45%,0.3)] transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div ref={field2Ref} className={`transition-all duration-200 ${field2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormField control={control} name="email" rules={{
            required: "L'email est requis",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Email invalide"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="votre@email.com" {...field} className="border-b-2 border-[hsl(221,70%,45%)] focus:border-[hsl(221,70%,40%)] focus:shadow-[0_2px_8px_hsla(221,70%,45%,0.3)] transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div ref={field3Ref} className={`transition-all duration-200 ${field3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormField control={control} name="phone" rules={{
            minLength: {
              value: 10,
              message: "Le téléphone doit contenir au moins 10 chiffres"
            },
            maxLength: {
              value: 20,
              message: "Le téléphone ne peut pas dépasser 20 caractères"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Téléphone (optionnel)</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(514) 123-4567" {...field} className="border-b-2 border-[hsl(221,70%,45%)] focus:border-[hsl(221,70%,40%)] focus:shadow-[0_2px_8px_hsla(221,70%,45%,0.3)] transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div ref={field4Ref} className={`transition-all duration-200 ${field4Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormField control={control} name="serviceType" rules={{
            required: "Le type de service est requis"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Type de service</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-9 w-full min-w-0 rounded-md border border-[hsl(221,70%,45%)] bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus:border-[hsl(221,70%,40%)] focus:shadow-[0_2px_8px_hsla(221,70%,45%,0.3)] disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="">Sélectionnez un service</option>
                      <option value="video">Vidéographie</option>
                      <option value="photo">Photographie</option>
                      <option value="promo">Promotion image de marque</option>
                      <option value="dj">Services DJ</option>
                      <option value="autre">Autre</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div ref={field5Ref} className={`transition-all duration-200 ${field5Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormField control={control} name="date" render={({
            field
          }) => <FormItem>
                  <FormLabel>Date souhaitée (optionnel)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} className="border-b-2 border-[hsl(221,70%,45%)] focus:border-[hsl(221,70%,40%)] focus:shadow-[0_2px_8px_hsla(221,70%,45%,0.3)] transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div ref={field6Ref} className={`transition-all duration-200 ${field6Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormField control={control} name="message" rules={{
            required: "Le message est requis",
            minLength: {
              value: 10,
              message: "Le message doit contenir au moins 10 caractères"
            },
            maxLength: {
              value: 1000,
              message: "Le message ne peut pas dépasser 1000 caractères"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Décrivez votre projet ou vos besoins..." {...field} className="min-h-32 border-2 border-[hsl(221,70%,45%)] focus:border-[hsl(221,70%,40%)] focus:shadow-[0_2px_8px_hsla(221,70%,45%,0.3)] transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
              <p className="text-red-800 font-medium">{submitError}</p>
            </div>}

          <div ref={buttonRef} className={`transition-all duration-200 ${buttonVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <Button type="submit" disabled={isSubmitting} className="w-full bg-[hsl(221,70%,45%)] text-white hover:scale-[1.03] hover:bg-[hsl(221,70%,40%)] active:bg-[hsl(221,70%,35%)] transition-all duration-[120ms] shadow-medium">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Envoi en cours...
                </> : "Envoyer le message"}
            </Button>
          </div>
        </div>
      </Form>
    </div>;
}